import React, { useEffect, useState } from "react";
import { api, fmtNum } from "@finance/lib/api";

export default function WorkingCapital() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/exec/working-capital").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const fmt = (v) => `TSh ${fmtNum(v)}`;
  return (
    <div className="mt-6" data-testid="working-capital">
      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="font-display text-base font-bold dark:text-white">Working Capital</h3>
        <span className="text-xs font-normal text-gray-400">· live from the GL</span>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Current Assets */}
        <div className="soft-card flex flex-col p-5 md:p-6" data-testid="wc-assets-card">
          <h4 className="lx-label text-gray-500">Current Assets</h4>
          <div className="mt-2 divide-y divide-gray-100 dark:divide-white/5">
            <LineRow label="Cash & Bank" value={fmt(d.cash_bank)} testid="wc-cash" />
            <LineRow label="Trade Receivables" value={fmt(d.trade_receivables)} testid="wc-ar" />
            <LineRow label="Unbilled AR (delivered, not invoiced)" value={fmt(d.unbilled_ar)} testid="wc-unbilled-ar" tone="pos" />
            <LineRow label="Prepayments (to suppliers)" value={fmt(d.prepayments)} testid="wc-prepayments" tone="pos" />
            <LineRow label="Inventory (AVCO)" value={fmt(d.inventory)} testid="wc-inventory" />
          </div>
          <div className="mt-auto pt-4">
            <div className="flex items-center justify-between rounded-lg bg-emerald-600/5 px-4 py-2.5" data-testid="wc-current-assets">
              <span className="lx-label text-emerald-700 dark:text-emerald-400">Total Current Assets</span>
              <span className="kpi-value text-lg !text-emerald-700 dark:!text-emerald-400">{fmt(d.current_assets)}</span>
            </div>
          </div>
        </div>

        {/* Current Liabilities */}
        <div className="soft-card flex flex-col p-5 md:p-6" data-testid="wc-liabilities-card">
          <h4 className="lx-label text-gray-500">Current Liabilities</h4>
          <div className="mt-2 divide-y divide-gray-100 dark:divide-white/5">
            <LineRow label="Trade Payables" value={fmt(d.trade_payables)} testid="wc-ap" tone="neg" />
            <LineRow label="Unbilled AP / GRNI (received, not billed)" value={fmt(d.unbilled_ap)} testid="wc-unbilled-ap" tone="neg" />
            <LineRow label="Advance Receipts (from clients)" value={fmt(d.advance_receipts)} testid="wc-advance-receipts" tone="neg" />
          </div>
          <div className="mt-auto space-y-3 pt-4">
            <div className="flex items-center justify-between rounded-lg bg-red-600/5 px-4 py-2.5" data-testid="wc-current-liabilities">
              <span className="lx-label text-red-700 dark:text-red-400">Total Current Liabilities</span>
              <span className="kpi-value text-lg !text-red-700 dark:!text-red-400">{fmt(d.current_liabilities)}</span>
            </div>
            <div className="rounded-lg bg-[#122E5C]/5 px-4 py-2.5" data-testid="wc-net">
              <div className="flex items-center justify-between">
                <span className="lx-label text-[#122E5C]">Net Working Capital</span>
                <span className={`kpi-value text-lg ${d.net_working_capital >= 0 ? "!text-[#122E5C]" : "!text-red-600"}`}>{fmt(d.net_working_capital)}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-gray-400">Current Assets − Current Liabilities</div>
            </div>
          </div>
        </div>

        {/* Order Pipeline */}
        <div className="soft-card flex flex-col p-5 md:p-6" data-testid="wc-pipeline-card">
          <h4 className="lx-label text-gray-500">Order Pipeline <span className="font-normal normal-case text-gray-400">· not yet on the balance sheet</span></h4>
          <div className="mt-2 divide-y divide-gray-100 dark:divide-white/5">
            <LineRow label="Open Client POs — expected cash IN" value={fmt(d.open_client_pos)} testid="wc-client-pos" tone="pos" />
            <LineRow label="Open Somcore/Supplier POs — expected cash OUT" value={fmt(d.open_lynx_pos)} testid="wc-lynx-pos" tone="neg" />
          </div>
          <div className="mt-auto pt-4">
            <div className="rounded-lg bg-[#1B4383]/5 px-4 py-2.5" data-testid="wc-projected">
              <div className="flex items-center justify-between">
                <span className="lx-label text-[#1B4383]">Projected Working Capital</span>
                <span className={`kpi-value text-lg ${d.projected_working_capital >= 0 ? "!text-[#1B4383]" : "!text-red-600"}`}>{fmt(d.projected_working_capital)}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-gray-400">Net WC + Client POs − Somcore POs</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LineRow({ label, value, testid, tone }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5" data-testid={testid}>
      <span className="lx-label text-gray-500">{label}</span>
      <span className={`kpi-value shrink-0 text-base ${tone === "neg" ? "!text-red-600" : tone === "pos" ? "!text-emerald-600" : ""}`}>{value}</span>
    </div>
  );
}
